import React, { useState, useEffect, useRef, useCallback } from 'react';
import Webcam from 'react-webcam'; // חובה לוודא שמותקן: npm install react-webcam
import { Phone, ArrowRight, Loader2, MessageSquare, HeartHandshake, Check, User, Calendar, Facebook, Mail, Camera, RefreshCw } from 'lucide-react';
import { 
  RecaptchaVerifier, 
  signInWithPhoneNumber, 
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence,
  linkWithPopup,           
  GoogleAuthProvider,      
  FacebookAuthProvider,     
  getAdditionalUserInfo    
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"; 
import { auth, db } from './firebase';

// בדיקת גיל (מעל 16)
function isOver16(dateString) {
  if (!dateString) return false;
  const birthDate = new Date(dateString);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
  }
  return age >= 16;
}

export default function AuthWizard({ onLoginSuccess, onNewUser, onBack }) {
  const [step, setStep] = useState(1); // 1=Phone, 2=OTP, 3=Details, 4=Selfie
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  
  // שדות להרשמה
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [faceImage, setFaceImage] = useState(null); // שמירת התמונה
  
  // ניהול מצב האימות החברתי
  const [isSocialLinked, setIsSocialLinked] = useState(false);
  const [linkedProvider, setLinkedProvider] = useState(null); 
  
  const [loading, setLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [rememberMe, setRememberMe] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);

  // מצלמה
  const webcamRef = useRef(null);
  
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          'size': 'invisible',
          'callback': () => {}
        });
      } catch (e) { console.error("Recaptcha init error:", e); }
    }
    return () => {
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); window.recaptchaVerifier = null; } catch(e) {}
      }
    };
  }, []);

  // --- שלב 1: שליחת SMS ---
  const handleSendCode = async () => {
    if (!phone || phone.length < 9) return alert("נא להזין מספר תקין");
    setLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const formattedPhone = phone.startsWith('+') ? phone : `+972${phone.replace(/^0/, '')}`;
      if (!window.recaptchaVerifier) window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { 'size': 'invisible' });
      
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
      setStep(2); 
    } catch (error) {
      console.error("SMS Error:", error);
      alert("שגיאה בשליחה. נסה לרענן את הדף.");
      if(window.recaptchaVerifier) { window.recaptchaVerifier.clear(); window.recaptchaVerifier = null; }
    } finally {
      setLoading(false);
    }
  };

  // --- שלב 2: אימות קוד ---
  const handleVerifyCode = async () => {
    if (otp.length < 6) return;
    setLoading(true);
    try {
      const result = await confirmationResult.confirm(otp);
      const uid = result.user.uid;
      setFirebaseUser(result.user);

      const userDocRef = doc(db, "users", uid);
      const userSnapshot = await getDoc(userDocRef);

      if (userSnapshot.exists()) {
        onLoginSuccess(userSnapshot.data());
      } else {
        // משתמש חדש - מעבר לשלב הפרטים
        setStep(3);
      }

    } catch (error) {
      console.error(error);
      alert("קוד שגוי או פג תוקף");
    } finally {
      setLoading(false);
    }
  };

  // --- שלב 3: חיבור חברתי ---
  const handleSocialLink = async (providerName) => {
    if (!auth.currentUser) return;
    setLoading(true);

    let provider;
    if (providerName === 'google') {
        provider = new GoogleAuthProvider();
        provider.addScope('https://www.googleapis.com/auth/user.birthday.read');
    } else {
        provider = new FacebookAuthProvider();
        provider.addScope('user_birthday');
    }

    try {
        const result = await linkWithPopup(auth.currentUser, provider);
        const details = getAdditionalUserInfo(result);
        
        if (details?.profile) {
            if (!fullName && details.profile.name) {
                setFullName(details.profile.name);
            }
        }

        setIsSocialLinked(true);
        setLinkedProvider(providerName);

    } catch (error) {
        console.error("Link Error:", error);
        if (error.code === 'auth/credential-already-in-use') {
            alert("החשבון הזה כבר מקושר למשתמש אחר במערכת.");
        } else {
            alert("שגיאה בחיבור החשבון: " + error.message);
        }
    } finally {
        setLoading(false);
    }
  };

  // מעבר לשלב הסלפי
  const handleContinueToSelfie = () => {
      if (!fullName || !birthDate) return alert("נא למלא שם ותאריך לידה");
      if (!isSocialLinked) return alert("חובה לחבר חשבון Google או Facebook");
      if (!isOver16(birthDate)) return alert("ההרשמה מותרת מגיל 16 ומעלה.");
      
      setStep(4); // מעבר לשלב המצלמה
  };

  // --- שלב 4: צילום סלפי ---
  const captureFace = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
        setFaceImage(imageSrc);
    }
  }, [webcamRef]);

  // --- סיום הרשמה (שמירה ב-DB) ---
  const handleFinalRegistration = async () => {
      if (!faceImage) return alert("חובה לצלם תמונת סלפי לאימות");

      setLoading(true);
      try {
          const userData = {
              uid: firebaseUser.uid,
              phoneNumber: firebaseUser.phoneNumber,
              fullName: fullName,
              birthDate: birthDate,
              linkedProvider: linkedProvider, 
              isVerified: true,
              profileImage: faceImage, // התמונה תשמש כתמונת פרופיל
              selfieImage: faceImage,  // וגם כתמונת אימות
              createdAt: serverTimestamp(),
              ratingSum: 0,
              reviewsCount: 0
          };

          await setDoc(doc(db, "users", firebaseUser.uid), userData);
          
          // כניסה לאפליקציה
          onLoginSuccess(userData);

      } catch (error) {
          console.error("Error saving user:", error);
          alert("שגיאה בשמירת הנתונים");
      } finally {
          setLoading(false);
      }
  };

  return (
    <div className="absolute inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-bottom h-[100dvh] overflow-y-auto" dir="rtl">
      <div id="recaptcha-container"></div>
      
      {step > 1 && (
        <button onClick={() => setStep(step - 1)} className="absolute top-4 left-4 p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition z-10">
            <ArrowRight size={20}/>
        </button>
      )}

      <div className="flex-1 flex flex-col px-6 pt-10 pb-40 max-w-md mx-auto w-full">
        
        {step === 1 && (
            <div className="text-center mb-6 animate-in fade-in zoom-in duration-500">
                <div className="bg-gradient-to-tr from-blue-600 to-purple-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg rotate-3">
                    <HeartHandshake className="text-white" size={32} />
                </div>
                <h1 className="text-3xl font-extrabold mb-1 text-gray-900">דרייבר.ישראל</h1>
                <p className="text-blue-600 font-bold text-sm mb-4">הפלטפורמה החברתית-כלכלית של ישראל</p>
            </div>
        )}

        <div className="bg-white">
            
            {/* שלב 1: טלפון */}
            {step === 1 && (
            <>
                <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2 px-1">מספר הטלפון שלך</label>
                    <div className="relative">
                        <Phone className="absolute top-4 right-4 text-gray-400" size={20}/>
                        <input 
                            type="tel" 
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            placeholder="050-0000000" 
                            className="w-full p-4 pr-12 bg-gray-50 border-2 border-gray-100 focus:border-black rounded-2xl text-xl font-bold outline-none transition-all dir-ltr text-right"
                            autoFocus
                        />
                    </div>
                </div>

                <div className="mb-6 flex items-center gap-3 px-1">
                    <label className="flex items-center gap-2 cursor-pointer select-none group">
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${rememberMe ? 'bg-black border-black' : 'border-gray-300 bg-white group-hover:border-gray-400'}`}>
                            {rememberMe && <Check size={14} className="text-white" strokeWidth={4} />}
                        </div>
                        <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="hidden"/>
                        <span className="text-sm font-bold text-gray-600 group-hover:text-black transition-colors">זכור אותי במכשיר זה</span>
                    </label>
                </div>
                
                <button onClick={handleSendCode} disabled={loading} className="w-full bg-black text-white py-4 rounded-xl font-bold shadow-xl active:scale-95 transition-transform flex items-center justify-center gap-2 text-lg">
                    {loading ? <Loader2 className="animate-spin"/> : "שלח לי קוד אימות"}
                </button>
            </>
            )}

            {/* שלב 2: קוד SMS */}
            {step === 2 && (
            <div className="text-center animate-in slide-in-from-right">
                <h1 className="text-2xl font-bold mb-2">קוד נשלח אלייך</h1>
                <p className="text-gray-500 mb-8">הזן את הקוד שקיבלת ל-{phone}</p>

                <div className="relative mb-6">
                    <input 
                        type="text" maxLength={6} value={otp} onChange={e => setOtp(e.target.value)}
                        placeholder="000000" 
                        className="w-full p-4 bg-gray-50 border-2 border-gray-200 focus:border-black rounded-2xl text-3xl tracking-[12px] text-center outline-none transition-all font-mono"
                        autoFocus
                    />
                </div>

                <button onClick={handleVerifyCode} disabled={loading || otp.length < 6} className="w-full bg-black text-white py-4 rounded-xl font-bold shadow-xl active:scale-95 transition-transform text-lg">
                    {loading ? <Loader2 className="animate-spin mx-auto"/> : "כניסה למערכת"}
                </button>
            </div>
            )}

            {/* שלב 3: חיבור חשבונות */}
            {step === 3 && (
            <div className="animate-in slide-in-from-right">
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <User className="text-blue-600" size={32} />
                    </div>
                    <h1 className="text-2xl font-bold mb-2">פרטים אישיים</h1>
                    <p className="text-gray-500">רק עוד רגע אנחנו שם...</p>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 px-1">שם מלא</label>
                        <div className="relative">
                            <User className="absolute top-4 right-4 text-gray-400" size={20}/>
                            <input 
                                type="text" 
                                value={fullName}
                                onChange={e => setFullName(e.target.value)}
                                placeholder="ישראל ישראלי" 
                                className="w-full p-4 pr-12 bg-gray-50 border-2 border-gray-100 focus:border-black rounded-2xl outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 px-1">אימות באמצעות חשבון</label>
                        {!isSocialLinked ? (
                            <div className="flex gap-3">
                                <button 
                                    onClick={() => handleSocialLink('google')}
                                    className="flex-1 py-3 bg-white border-2 border-gray-200 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                                >
                                    <Mail className="text-red-500" size={20}/> גוגל
                                </button>
                                <button 
                                    onClick={() => handleSocialLink('facebook')}
                                    className="flex-1 py-3 bg-blue-600 text-white rounded-xl flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors shadow-md"
                                >
                                    <Facebook size={20}/> פייסבוק
                                </button>
                            </div>
                        ) : (
                            <div className="bg-green-50 border border-green-200 p-4 rounded-xl flex items-center gap-3 text-green-700 font-bold">
                                <Check size={20} className="bg-green-200 p-1 rounded-full"/>
                                חשבון מחובר בהצלחה!
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 px-1">תאריך לידה</label>
                        <div className="relative">
                            <Calendar className="absolute top-4 right-4 text-gray-400" size={20}/>
                            <input 
                                type="date" 
                                value={birthDate}
                                onChange={e => setBirthDate(e.target.value)}
                                className="w-full p-4 pr-12 bg-gray-50 border-2 border-gray-100 focus:border-black rounded-2xl outline-none transition-all"
                            />
                        </div>
                    </div>

                    <button 
                        onClick={handleContinueToSelfie} 
                        className={`w-full mt-6 py-4 rounded-xl font-bold shadow-xl active:scale-95 transition-transform text-lg flex items-center justify-center gap-2
                        ${(isSocialLinked && fullName && birthDate) ? 'bg-black text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                    >
                        המשך לצילום תמונה <ArrowRight size={20}/>
                    </button>
                </div>
            </div>
            )}

            {/* שלב 4: צילום סלפי (חדש!) */}
            {step === 4 && (
            <div className="animate-in slide-in-from-right text-center">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold mb-2">תמונה לפרופיל</h1>
                    <p className="text-gray-500">כדי שאנשים יזהו את מי שעוזר להם :)</p>
                </div>

                <div className="relative w-64 h-64 mx-auto mb-6 bg-gray-100 rounded-full overflow-hidden border-4 border-black shadow-xl">
                    {!faceImage ? (
                        <Webcam 
                            ref={webcamRef} 
                            audio={false} 
                            screenshotFormat="image/jpeg" 
                            videoConstraints={{ facingMode: "user" }}
                            className="w-full h-full object-cover" 
                        />
                    ) : (
                        <img src={faceImage} className="w-full h-full object-cover" />
                    )}
                </div>

                {!faceImage ? (
                    <button onClick={captureFace} className="w-full bg-black text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
                        <Camera size={20}/> צלם תמונה
                    </button>
                ) : (
                    <div className="space-y-3">
                        <button onClick={handleFinalRegistration} disabled={loading} className="w-full bg-green-600 text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform text-lg">
                            {loading ? <Loader2 className="animate-spin"/> : "סיים והרשם"}
                        </button>
                        <button onClick={() => setFaceImage(null)} className="w-full bg-gray-100 text-gray-600 py-3 rounded-xl font-bold flex items-center justify-center gap-2">
                            <RefreshCw size={16}/> צלם שוב
                        </button>
                    </div>
                )}
            </div>
            )}

        </div>
      </div>
    </div>
  );
}